import sys from '@truckdown/jsclient';
import * as icons from '@truckdown/jsclient/icons';
import * as apis from '@truckdown/jsclient/apis';
import { defineComponent, ref, computed, onMounted, onUnmounted, Ref, ComputedRef, nextTick } from 'vue';

import * as locations from '@truckdown/locations';

import type_brand from '../Images/type_brand.png';
import type_pro from '../Images/type_pro.png';
import type_elite from '../Images/type_elite.png';
import type_msp from '../Images/type_msp.png';
import type_mspar from '../Images/type_mspar.png';
import { ILegacyListingService } from '../Services/ILegacyListingService';


import defaultPin from '../Images/default-pin.png';
import defaultPreferredPin from '../Images/default-p-pin.png';

import preferredPin from '../Images/preferred-pin.png';
import preferredPreferredPin from '../Images/preferred-p-pin.png';

import elitePin from '../Images/elite-pin.png';
import elitePreferredPin from '../Images/elite-p-pin.png';

import proPin from '../Images/pro-pin.png';
import proPreferredPin from '../Images/pro-p-pin.png';

import brandedPin from '../Images/branded-pin.png';
import brandedPreferredPin from '../Images/branded-p-pin.png';

import mspPin from '../Images/msp-pin.png';
import mspPreferredPin from '../Images/msp-p-pin.png';
import msparPin from '../Images/mspar-pin.png';
import msparPreferredPin from '../Images/mspar-p-pin.png';
import kyrishPin from '../Images/kyrish-pin.png';
import kyrishPreferredPin from '../Images/kyrish-p-pin.png';
import rechtienPin from '../Images/rechtien-pin.png';
import rechtienPreferredPin from '../Images/rechtien-p-pin.png';
import trivistaPin from '../Images/trivista-pin.png';
import trivistaPreferredPin from '../Images/trivista-p-pin.png';
import borderPin from '../Images/border-pin.png';
import borderPreferredPin from '../Images/border-p-pin.png';

import americanexpress from '../Images/americanexpress.png';
import cbcharge from '../Images/cbcharge.png';
import comdata from '../Images/comdata.png';
import discover from '../Images/discover.png';
import efs from '../Images/efs.png';
import mastercard from '../Images/mastercard.png';
import visa from '../Images/visa.png';

import { ILocationsUtilities } from '../Services/ILocationsUtilities';

sys.fontLibrary.add(icons.solid.faBars);
sys.fontLibrary.add(icons.solid.faShare);
sys.fontLibrary.add(icons.solid.faMap);
sys.fontLibrary.add(icons.solid.faSmile);
sys.fontLibrary.add(icons.solid.faThumbsUp);
sys.fontLibrary.add(icons.solid.faThumbsDown);
sys.fontLibrary.add(icons.solid.faWrench);
sys.fontLibrary.add(icons.solid.faStickyNote);
sys.fontLibrary.add(icons.solid.faStar);
sys.fontLibrary.add(icons.solid.faCloud);
sys.fontLibrary.add(icons.solid.faPhone);

sys.fontLibrary.add(icons.solid.faExclamationTriangle);
sys.fontLibrary.add(icons.solid.faEye);
sys.fontLibrary.add(icons.solid.faPencil);
sys.fontLibrary.add(icons.solid.faCalendarDays);
sys.fontLibrary.add(icons.solid.faCircleInfo);

sys.fontLibrary.add(icons.regular.faCircleCheck);
sys.fontLibrary.add(icons.regular.faStar);
sys.fontLibrary.add(icons.regular.faStarHalfStroke);
sys.fontLibrary.add(icons.solid.faScrewdriverWrench);
sys.fontLibrary.add(icons.solid.faCircleCheck);
sys.fontLibrary.add(icons.solid.faCaretLeft);
sys.fontLibrary.add(icons.solid.faCaretRight);

sys.fontLibrary.add(icons.solid.faCircleUp);
sys.fontLibrary.add(icons.solid.faXmark);


interface IHeader {
    closeDropdown: (id?: string) => void,
    showMenu: Ref<boolean>,
    displayName: ComputedRef<string>,
    headerIcon: ComputedRef<string>,
    headerIconStyle: ComputedRef<string>,
    toggleMenu: (ev: Event) => void,
    userInfo: ComputedRef<{ isAuthenticated: boolean, isOwned: boolean, isFleet: boolean, isAdmin: boolean }>
}

const setupHeader = function (listing: locations.Listing) {
    const showMenu = ref<boolean>(false);

    const isPreferred = computed(() => {
        return listing.properties.preference.type == locations.PreferenceType.Preferred;
    });
    const isAvoided = computed(() => {
        return listing.properties.preference.type == locations.PreferenceType.Avoid;
    });

    var isOwned = sys.framework.context.isInRole(apis.framework.SecurityConstants.Roles.CustomerAdminRole)
        && listing.accountIds.indexOf(sys.framework.context.user?.accountId ?? '') >= 0;

    var canUpgrade = function () {
        if (isOwned) {
            if (listing.properties.type == locations.ListingType.Elite) {
                return false;
            }
            return true;
        }

        return false;
    }

    const userInfo = computed(() => {
        return {
            isAuthenticated: sys.framework.context.isAuthenticated(),
            isOwned: isOwned,
            isFleet: sys.framework.context.isInRole(apis.framework.SecurityConstants.Roles.FleetUserRole),
            isAdmin: (sys.framework.context.isInRole(apis.framework.SecurityConstants.Roles.CSRRole)
                || sys.framework.context.isInRole(apis.framework.SecurityConstants.Roles.SystemRole)),
            canUpgrade: canUpgrade()
        };
    });

    const closeDropdown = function (id?: string) {
        if (!id || id !== listing.id) {
            showMenu.value = false;
        }
    };

    const displayName = computed(() => {
        if (listing.properties.private) {
            return listing.name + ' (private)';
        }
        if (listing.properties.type === locations.ListingType.None) {
            return listing.name + ' (not published)';
        }
        return listing.name;
    });

    const headerIcon = computed(() => {
        if (listing.properties.useCustomIcon) {
            if (listing.properties.customClass === 'msp') {
                return type_msp;
            }
            if (listing.properties.customClass === 'mspar') {
                return type_mspar;
            }
        }

        switch (listing.properties.type) {
            case (locations.ListingType.Elite):
                return type_elite;

            case (locations.ListingType.Pro):
                return type_pro;

            case (locations.ListingType.Branded):
                return type_brand;

            default:
                return '';
        }
    });

    const headerIconStyle = computed(() => {
        let icon = headerIcon.value;
        if (icon.length > 0) {
            return 'background: url(' + icon + ') center center no-repeat;' + listing.properties.iconStyle;
        }
        return '';
    });

    const toggleMenu = function (ev: Event): void {
        showMenu.value = !showMenu.value;
        if (showMenu.value) {
            sys.events.emit('close-dropdowns', listing.id);
        }
    }

    onMounted(() => {
        sys.events.on('close-dropdowns', closeDropdown);
    });

    onUnmounted(() => {
        sys.events.off('close-dropdowns', closeDropdown);
    });

    return {
        closeDropdown,
        showMenu,
        displayName,
        headerIcon,
        headerIconStyle,
        toggleMenu,
        userInfo,
        isPreferred,
        isAvoided
    } as IHeader;
};

const setupFooter = function (header: IHeader, listing: locations.Listing) {
    const footerClass = ref<string>('footer');

    if (!header.userInfo.value.isAuthenticated) {
        footerClass.value = 'footer anonymous';
    }

    if (header.userInfo.value.isFleet) {
        footerClass.value = 'footer fleet';
    }

    return {
        footerClass
    }
}

const setupShowNumbers = function (listing: locations.Listing) {
    var service = sys.framework.getRequiredService<locations.ILocationActionsService>('ILocationActionsService');

    const phoneNumbers = ref<locations.ListingPhoneNumber[] | null>(null);
    const captchaInstance = ref<string>(listing.id + '-captcha');
    const displayCaptcha = ref<boolean>(false);
    const isBlocked = ref<boolean>(false);
    const captchaWarning = ref<string | undefined>();

    const hideNumbers = ref<boolean>(false);

    const showNumbers = computed(() => {
        if (phoneNumbers.value != null && phoneNumbers.value.length > 0 && !hideNumbers.value) {
            return true;
        }
        return false;
    });

    const loadNumbers = async function () {
        sys.events.emit('close-dropdowns');
        hideNumbers.value = false;

        if (phoneNumbers.value != null && phoneNumbers.value.length > 0) {
            sys.events.emit('show-numbers', listing.id);
        } else {
            var res = await service.addCall(listing.id);
            captchaWarning.value = undefined;

            if (res.blocked) {
                isBlocked.value = true;
            } else if (res.captcha) {
                displayCaptcha.value = true;
            } else {
                if ((res.warning?.length ?? 0) > 0) {
                    captchaWarning.value = res.warning;
                };
                phoneNumbers.value = res.result;

                sys.events.emit('refresh-call-history');
                sys.events.emit('show-numbers', listing.id);

                (window as any).gtag('event', 'Call', {
                    'event_category': 'Listing',
                    'event_label': 'Call'
                });
            }
        }
    };

    onMounted(() => {
        sys.events.on('captcha-success', function (val: string) {
            if (captchaInstance.value == val) {
                displayCaptcha.value = false;
                loadNumbers();
            }
        });

        sys.events.on('show-numbers', function (id: string) {
            if (listing.id != id) {
                hideNumbers.value = true;
            }
        });
    });

    return {
        phoneNumbers,
        showNumbers,
        loadNumbers,
        captchaInstance,
        displayCaptcha,
        isBlocked,
        captchaWarning
    };
};

const setupListingLinks = function (listing: locations.Listing) {
    const getUrl = function (path: string) {
        let currentUrl = window.location.pathname + window.location.search + window.location.hash;
        return path + '?returnUrl=' + encodeURIComponent(currentUrl);
    }

    const detailLink = computed(() => {
        var name = listing.name.toLowerCase().replace(/\s/g, '_').replace(/[^\w_]/g, '');
        var city = listing.location.city.toLowerCase().replace(/\s/g, '_').replace(/[^\w_]/g, '');
        var state = listing.location.regionCode.toLowerCase().replace(/\s/g, '_').replace(/[^\w_]/g, '');
        return '/search/view-detail/' + encodeURIComponent(name + '_' + city + '_' + state) + '/' + encodeURIComponent(listing.id);
    });

    const viewDetails = function (ev: Event) {
        sys.events.emit('close-dropdowns');
        window.location.href = detailLink.value;
    }

    const viewLocationLink = function () {
        return '/location-admin/view-location/' + encodeURIComponent(listing.id);
    }

    const editLocationLink = function () {
        return '/location-admin/edit-location/' + encodeURIComponent(listing.id);
    }

    const viewAccountLink = function () {
        return '/accounts-admin/view-account/' + encodeURIComponent(listing.accountId);
    }

    const editHoursLink = function () {
        return '/location/admin/hours/' + encodeURIComponent(listing.id);
    }

    const customerEditLocationLink = function () {
        return '/locations/edit-location/' + encodeURIComponent(listing.id);
    }

    const customerEditHoursLink = function () {
        return '/location/hours/' + encodeURIComponent(listing.id);
    }

    const viewLocation = function (ev: Event) {
        sys.events.emit('close-dropdowns');
        window.location.href = viewLocationLink();
    }

    const editLocation = function (ev: Event) {
        sys.events.emit('close-dropdowns');
        window.location.href = editLocationLink()
    }

    const viewAccount = function (ev: Event) {
        sys.events.emit('close-dropdowns');
        window.location.href = viewAccountLink()
    }

    const editHours = function (ev: Event) {
        console.log('Routing to edit hours');
        sys.events.emit('close-dropdowns');
        window.location.href = editHoursLink()
    }

    const customerEditLocation = function (ev: Event) {
        sys.events.emit('close-dropdowns');
        window.location.href = customerEditLocationLink()
    }

    const customerEditHours = function (ev: Event) {
        sys.events.emit('close-dropdowns');
        window.location.href = customerEditHoursLink()
    }

    const viewWebsite = async function (ev: Event) {
        sys.events.emit('close-dropdowns');
        var service = sys.framework.getRequiredService<locations.ILocationActionsService>('ILocationActionsService');
        await service.viewWebsite(listing.id);
        window.open(listing.properties.website);
    }

    return {
        detailLink,
        viewDetails,
        viewWebsite,
        viewLocationLink,
        editLocationLink,
        viewAccountLink,
        editHoursLink,
        viewLocation,
        editLocation,
        viewAccount,
        editHours,
        getUrl,
        customerEditLocationLink,
        customerEditHoursLink,
        customerEditLocation,
        customerEditHours
    };
};

const setupActions = function (listing: locations.Listing) {
    const openFormId = ref<string>('');

    const buttonClass = function (form: string, extra: string, requireFleet: boolean = false) {
        if (!sys.framework.context.isAuthenticated()) {
            extra = 'disabled ' + extra;
        }

        if (requireFleet && !sys.framework.context.isInRole(apis.framework.SecurityConstants.Roles.FleetUserRole)) {
            extra = 'disabled ' + extra;
        }

        if (openFormId.value == form) {
            return extra + ' active';
        } else {
            return extra;
        }
    }

    const showOnMap = function (ev: Event) {
        sys.events.emit('close-dropdowns');
        openFormId.value = '';
        sys.events.emit('show-on-map', listing);
    }

    const submitAnalytics = async function (fact:string) {
        var service = sys.framework.getRequiredService<locations.ILocationActionsService>('ILocationActionsService');
        var model: locations.AddLocationAnalyticModel = {
            locationId: listing.id,
            cube: 'location',
            facts: [
                { "n": fact, "v": 1 }
            ],
            dimensions: [],
            drilledDimensions: [],
        };
        await service.addLocationAnalytic(model);
    }

    const shareLocation = async function (ev: Event) {
        if (checkIfDisabled(ev)) {
            sys.events.emit('close-dropdowns');
            if (openFormId.value == 'forwardForm') {
                openFormId.value = '';
            } else {
                openFormId.value = 'forwardForm';
                await submitAnalytics('bs');
            }
        }
    }

    const ratingInitialValue = ref<number | undefined>();

    const rateService = async function (ev: Event, initialValue?: number) {
        if (checkIfDisabled(ev)) {
            sys.events.emit('close-dropdowns');
            ratingInitialValue.value = initialValue;
            if (openFormId.value == 'rateServiceForm') {
                openFormId.value = '';
            } else {
                openFormId.value = 'rateServiceForm';
                await submitAnalytics('br');
            }
        }
    }

    const editPreferred = async function (ev: Event) {
        //upgrade option
        if (checkIfDisabled(ev)) {
            sys.events.emit('close-dropdowns');
            if (openFormId.value == 'favoriteForm') {
                openFormId.value = '';
            } else {
                openFormId.value = 'favoriteForm';
                await submitAnalytics('bfav');
            }
        }
    }

    const addDnu = async function (ev: Event) {
        //upgrade option
        if (checkIfDisabled(ev)) {
            sys.events.emit('close-dropdowns');
            if (openFormId.value == 'avoidForm') {
                openFormId.value = '';
            } else {
                openFormId.value = 'avoidForm';
                await submitAnalytics('bav');
            }
        }
    }

    const viewNotes = async function (ev: Event) {
        if (checkIfDisabled(ev)) {
            sys.events.emit('close-dropdowns');
            if (openFormId.value == 'noteForm') {
                openFormId.value = '';
            } else {
                openFormId.value = 'noteForm';
                await submitAnalytics('bn');
            }
        }
    }

    const reportLocation = function (ev: Event) {
        sys.events.emit('close-dropdowns');
        if (openFormId.value == 'updateInfoForm') {
            openFormId.value = '';
        } else {
            openFormId.value = 'updateInfoForm';
        }
    }

    const closeListingForm = function (id: string) {
        if (id == listing.id) {
            openFormId.value = '';
        }
    };

    const updateNotes = async function (id: string, notes?: locations.ListingNote[]) {
        if (id == listing.id) {
            if (!notes) {
                var service = sys.framework.getRequiredService<ILegacyListingService>('ILegacyListingService');
                let res = await service.getNotes(id);
                listing.notes = res.map((note) => {
                    return {
                        id: note._id,
                        comment: note.c,
                        date: note.d,
                        user: note.n,
                        userId: note.uid
                    } as locations.ListingNote;
                });
            } else {
                listing.notes = notes;
            }
        }
    };

    const updatePreference = async function (id: string, preference: locations.PreferenceModel) {
        if (id == listing.id && preference && preference != null) {
            listing.properties.preference = preference;
        }
    };

    const viewServices = async function (ev: Event) {
        sys.events.emit('close-dropdowns');
        if (openFormId.value == 'services') {
            openFormId.value = '';
        } else {
            openFormId.value = 'services';
            await submitAnalytics('bsvs');
        }
    }

    const removeRequest = function (ev: Event) {
        sys.events.emit('close-dropdowns');
        submitMessage.value = '';
        if (openFormId.value == 'removeRequest') {
            openFormId.value = '';
        } else {
            openFormId.value = 'removeRequest';
        }
    }

    const upgradeCheckValue = ref<boolean | undefined>();

    const upgradeRequest = async function (ev: Event) {
        sys.events.emit('close-dropdowns');

        if (!upgradeCheckValue.value) {
            var service = sys.framework.getRequiredService<ILegacyListingService>('ILegacyListingService');
            upgradeCheckValue.value = await service.canUpgradeLocation(sys.framework.context.user?.accountId ?? '');
        }
        
        if (upgradeCheckValue.value) {
            //redirect
            window.location.href = '/shop/location-services/upgrade-location/' + encodeURIComponent(listing.id);
        } else {
            submitMessage.value = '';
            if (openFormId.value == 'upgradeRequest') {
                openFormId.value = '';
            } else {
                openFormId.value = 'upgradeRequest';
            }
        }
    }

    onMounted(() => {
        sys.events.on('close-listing-form', closeListingForm);
        sys.events.on('update-notes', updateNotes);
        sys.events.on('update-preference', updatePreference);
    });

    onUnmounted(() => {
        sys.events.off('close-listing-form', closeListingForm);
        sys.events.off('update-notes', updateNotes);
        sys.events.off('update-preference', updatePreference);
    });

    const checkIfDisabled = function (ev: Event) {
        if (ev.target instanceof HTMLButtonElement && ev.target.disabled) {
            return false;
        } else {
            return true;
        }
    }

    const checkIfParentDisabled = function (ev: Event) {
        if (ev.target instanceof HTMLElement && (ev.target as HTMLElement).parentNode) {
            const parentElement = (ev.target as HTMLElement).parentNode;
            if (parentElement instanceof HTMLElement && parentElement.hasAttribute('disabled')) {
                return false;
            } else {
                return true;
            }
        } 
    }

    const submitMessage = ref<string>('');

    const suppressRemove = ref<boolean>(false);
    const suppressUpgrade = ref<boolean>(listing.properties.type == locations.ListingType.Elite);

    const submitRemoveRequest = async function (ev: Event) {
        var service = sys.framework.getRequiredService<locations.ILocationActionsService>('ILocationActionsService');
        var res = await service.submitRemoveLocationRequest(listing.id);
        if (res.ok) {
            suppressRemove.value = true;
            closeListingForm(listing.id);
        } else {
            submitMessage.value = 'There was an error submitting your request.  ' + res.message;
        }
    }

    const submitUpgradeRequest = async function (ev: Event) {
        var service = sys.framework.getRequiredService<locations.ILocationActionsService>('ILocationActionsService');
        var res = await service.submitUpdradeLocationRequest(listing.id);
        if (res.ok) {
            suppressUpgrade.value = true;
            closeListingForm(listing.id);
        } else {
            submitMessage.value = 'There was an error submitting your request.  ' + res.message;
        }
    }

    return {
        shareLocation,
        showOnMap,
        rateService,
        ratingInitialValue,
        editPreferred,
        addDnu,
        viewNotes,
        reportLocation,
        openFormId,
        buttonClass,
        viewServices,
        removeRequest,
        upgradeRequest,
        closeListingForm,
        submitMessage,
        submitRemoveRequest,
        submitUpgradeRequest,
        suppressRemove,
        suppressUpgrade,
        upgradeCheckValue
    };
};

const setupMap = function (listing: locations.Listing) {
    const showMap = ref<boolean>(false);
    const showMarker = ref<boolean>(false);
    const mapCenter = computed(() => {
        return { lat: listing.location.lat, lng: listing.location.lng };
    });

    //TODO: logic is duplicated in ActivityBoard
    const mapGetIcon = function () {
        if (listing.properties.useCustomPin) {
            if (listing.properties.customClass == 'msp') {
                if (listing.properties.preference?.type == locations.PreferenceType.Preferred) {
                    return mspPreferredPin;
                } else {
                    return mspPin;
                }
            }
            if (listing.properties.customClass == 'mspar') {
                if (listing.properties.preference?.type == locations.PreferenceType.Preferred) {
                    return msparPreferredPin;
                } else {
                    return msparPin;
                }
            }
            if (listing.properties.customClass == 'kyrish') {
                if (listing.properties.preference?.type == locations.PreferenceType.Preferred) {
                    return kyrishPreferredPin;
                } else {
                    return kyrishPin;
                }
            }
            if (listing.properties.customClass == 'rechtien') {
                if (listing.properties.preference?.type == locations.PreferenceType.Preferred) {
                    return rechtienPreferredPin;
                } else {
                    return rechtienPin;
                }
            }
            if (listing.properties.customClass == 'border') {
                if (listing.properties.preference?.type == locations.PreferenceType.Preferred) {
                    return borderPreferredPin;
                } else {
                    return borderPin;
                }
            }
        }

        switch (listing.properties.template) {
            case locations.ListingTemplate.TDPreferred:
                if (listing.properties.preference?.type == locations.PreferenceType.Preferred) {
                    return preferredPreferredPin;
                } else {
                    return preferredPin;
                }
            case locations.ListingTemplate.Elite:
                if (listing.properties.preference?.type == locations.PreferenceType.Preferred) {
                    return elitePreferredPin;
                } else {
                    return elitePin;
                }
            case locations.ListingTemplate.Pro:
                if (listing.properties.preference?.type == locations.PreferenceType.Preferred) {
                    return proPreferredPin;
                } else {
                    return proPin;
                }
            case locations.ListingTemplate.Branded:
                if (listing.properties.preference?.type == locations.PreferenceType.Preferred) {
                    return brandedPreferredPin;
                } else {
                    return brandedPin;
                }
            default:
                if (listing.properties.preference?.type == locations.PreferenceType.Preferred) {
                    return defaultPreferredPin;
                } else {
                    return defaultPin;
                }
        }
    }

    const toggleMarker = function () {
        showMarker.value = true;
    }

    onMounted(() => {
        sys.events.on('show-on-map', async function (showListing: locations.Listing) {
            if (showListing.id == listing.id) {
                showMap.value = !showMap.value;
                if (showMap.value) {
                    var service = sys.framework.getRequiredService<locations.ILocationActionsService>('ILocationActionsService');
                    await service.showOnMap(listing.id);

                    (window as any).gtag('event', 'Map', {
                        'event_category': 'Listing',
                        'event_label': 'Show'
                    });
                }
            }
        });
    });

    return {
        showMap,
        showMarker,
        mapCenter,
        toggleMarker,
        mapGetIcon
    };
}

const setupDetails = function (listing: locations.Listing) {
    let utilities = sys.framework.getRequiredService<ILocationsUtilities>('ILocationsUtilities');
    let activeIndex = ref<number>(0);
    const carouselRef = ref<HTMLElement | null>(null);

    const showDescription = computed(() => {
        return listing.properties.description
            && listing.properties.description.length > 0;
    });
    const showDealers = computed(() => {
        return listing.networks.length > 0;
    });
    const showPaymentTypes = computed(() => {
        return listing.properties.paymentTypes.length > 0;
    });
    const showImages = computed(() => {
        return listing.properties.photoUrls.length > 0;
    });

    const listingServices = ref<locations.ListingService[]>([]);

    const isServiceSelected = function (service: locations.ListingService) {
        return listing.publishServices.some((s) => { return s.code == service.code; });
    };

    const getPaymentImage = function (type: locations.PaymentType) {
        switch (type) {
            case locations.PaymentType.AmericanExpress:
                return americanexpress;

            case locations.PaymentType.cbCharge:
                return cbcharge;

            case locations.PaymentType.ComData:
                return comdata;

            case locations.PaymentType.Discover:
                return discover;

            case locations.PaymentType.EFS:
                return efs;

            case locations.PaymentType.Mastercard:
                return mastercard;

            case locations.PaymentType.Visa:
                return visa;
        }
    };

    const getPaymentName = function (type: locations.PaymentType) {
        switch (type) {
            case locations.PaymentType.AmericanExpress:
                return "American Express";

            case locations.PaymentType.cbCharge:
                return "cbCharge";

            case locations.PaymentType.ComData:
                return "Comdata";

            case locations.PaymentType.Discover:
                return "Discover";

            case locations.PaymentType.EFS:
                return "EFS";

            case locations.PaymentType.Mastercard:
                return "Mastercard";

            case locations.PaymentType.Visa:
                return "Visa";
        }
    };

    const getAmentities = function (service: locations.ListingService): locations.ListingService[] {
        var svs = utilities.services.find((s) => {
            return s.code == service.code;
        });

        if (!svs) {
            return []
        }

        var children = utilities.services.filter((s) => {
            return (svs?.childServices.indexOf(s.code) ?? -1) >= 0;
        });

        var getDisplay = function (service: apis.tools.Service) {
            let addData = listing.additionalData.find((d) => { return d.isAmentity && d.key == service.code; });
            if (addData && addData.value && addData.value != null) {
                return service.name + ' (' + addData.value + ')';
            }

            return service.name;
        }

        return children.filter((s) => {
            if (listing.services.some((ls) => { return ls.code == s.code; })) {
                return true;
            }

            return false;
        }).map((s) => {
            return {
                code: s.code,
                name: getDisplay(s)
            } as locations.ListingService;
        });
    }

    const additionalData = computed(() => {
        return listing.additionalData
            .filter((d) => { return !d.isAmentity; });
    });

    const hasAdditionalData = computed(() => {
        if (sys.framework.context.isInRole(apis.framework.SecurityConstants.Roles.FleetUserRole)) {
            return true;
        }

        if (additionalData.value.length > 0) {
            return true;
        }

        return false;
    });

    const selectedImage = ref<string>('');

    const showImage = function (image: string) {
        selectedImage.value = image;
        console.log("selected", selectedImage.value);
    }

    onMounted(async () => {
        await utilities.loadServices;
        listingServices.value = utilities.services.filter((s) => {
            if (s.type != apis.tools.ServiceType.Service) {
                return false;
            }

            if (listing.services.some((ls) => { return ls.code == s.code; })) {
                return true;
            }

            return false;
        }).map((s) => {
            return {
                code: s.code,
                name: s.name
            } as locations.ListingService;
        });
    });

    const goToSlide = (index: number) => {
        activeIndex.value = index;
    }

    const prevImage = (length:number) => {
        activeIndex.value = (activeIndex.value - 1 + length) % length;
    };

    const nextImage = (length: number) => {
        activeIndex.value = (activeIndex.value + 1) % length;
    };



    return {
        showDescription,
        showDealers,
        showPaymentTypes,
        listingServices,
        getAmentities,
        isServiceSelected,
        showImages,
        getPaymentImage,
        getPaymentName,
        showImage,
        selectedImage,
        additionalData,
        hasAdditionalData,
        activeIndex,
        goToSlide,
        carouselRef,
        nextImage,
        prevImage
    };
}

export default defineComponent({
    props: {
        listing: {
            type: Object as () => locations.Listing,
            required: true
        },
        isdetails: {
            type: Boolean,
            default: false
        }
    },
    setup: function (props, ctx) {
        let listing = props.listing as locations.Listing;

        const header = setupHeader(listing);
        const showNumbersObject = setupShowNumbers(listing);
        const listingLinks = setupListingLinks(listing);
        const map = setupMap(listing);
        const isPlanRV = sys.framework.context.sys == 'planrv' || false;

        //LISTING DATA

        const listingClass = computed(() => {
            let customClass = isPremiumListing.value ? ' premium' : '';

            let mainClass = ' listing';
            if (isPlanRV) {
                mainClass = ' listing planrv';
            }

            if (listing.properties.customClass && listing.properties.customClass.length > 0) {
                customClass = customClass + ' ' + listing.properties.customClass;
            }

            if (listing.properties.template == locations.ListingTemplate.Owned)
                return locations.ListingTemplate[locations.ListingTemplate.Stock].toLowerCase() + mainClass + customClass;
            return locations.ListingTemplate[listing.properties.template].toLowerCase() + mainClass + customClass;
        });

        const isPremiumListing = computed(() => {
            return listing.properties.type !== locations.ListingType.Stock
                && listing.properties.type !== locations.ListingType.None;
        });

        const preferredLabel = computed(() => {
            if (listing.properties.preference.type == locations.PreferenceType.None) {
                return 'Add to Favorites List';
            }
            return 'Edit Favorite Settings';
        });

        const noteLabel = computed(() => {
            if (listing.notes.length > 0) {
                return 'View Notes';
            }
            return 'Add Note';
        });

        const ratingTitle = computed(() => {
            if (listing.properties.rating.count > 0) {
                return listing.properties.rating.value + ' out of 5 - ' + listing.properties.rating.count + ' ratings.';
            }
            return '';
        });

        const noteCount = computed(() => {
            if (listing.notes.length == 0) {
                return '';
            } else if (listing.notes.length > 9) {
                return '9+';
            } else {
                return '' + listing.notes.length;
            }
        });

        const listingDescription = computed(() => {
            if (listing.properties.description && listing.properties.description.length > 0) {
                var div = document.createElement("div");
                div.innerHTML = listing.properties.description;
                return div.textContent || div.innerText || listing.name;
            }

            return listing.name;
        });

        onMounted(() => {
            if (props.isdetails) {
                (window as any).gtag('event', 'View', {
                    'event_category': 'Listing',
                    'event_label': 'Details'
                });
            }
        });

        return {
            //LISTING DATA
            listingClass,
            isPremiumListing,
            preferredLabel,
            noteLabel,
            ratingTitle,
            noteCount,
            listingDescription,
            ...map,
            ...setupDetails(listing),
            ...showNumbersObject,
            ...listingLinks,
            ...header,
            ...setupFooter(header, listing),
            ...setupActions(listing)
        };
    }
});
